import { ArrowRight, CheckCircle2, RefreshCw, Sparkles, Trophy } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { money } from "../../shared/domain";
import type { EarnSnapshot, MemberOffer } from "../../shared/types";
import { OfferCard } from "../components/OfferCard";
import { ProgressBar } from "../components/ProgressBar";
import { Spinner } from "../components/Spinner";
import { api } from "../lib/api";

type View = "quick" | "highest" | "free";

export function EarnPage() {
  const [snapshot, setSnapshot] = useState<EarnSnapshot | null>(null);
  const [error, setError] = useState("");
  const [view, setView] = useState<View>("quick");
  const [busyOffer, setBusyOffer] = useState<string>();
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setError("");
    try {
      setSnapshot(await api.earn());
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "We couldn't load your account.");
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const recommendation = snapshot?.offers.find((offer) => offer.id === snapshot.recommendation?.offerId);
  const secondary = useMemo(() => {
    if (!snapshot) return [];
    const eligible = snapshot.offers.filter((offer) => offer.id !== recommendation?.id && offer.memberState === "available");
    if (view === "quick") return eligible.filter((offer) => offer.quickWin).sort((a, b) => (a.estimatedMinutes || 999) - (b.estimatedMinutes || 999));
    if (view === "free") return eligible.filter((offer) => !offer.purchaseRequired).sort((a, b) => b.rewardCents - a.rewardCents);
    return eligible.sort((a, b) => b.rewardCents - a.rewardCents);
  }, [recommendation?.id, snapshot, view]);

  const start = async (offer: MemberOffer, placement = "secondary") => {
    setBusyOffer(offer.id);
    setError("");
    try {
      const result = await api.startOffer(offer.id, placement, snapshot?.recommendation?.offerId === offer.id ? snapshot.recommendation.reason : undefined);
      window.location.assign(result.redirectUrl);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Unable to open this offer.");
      setBusyOffer(undefined);
    }
  };

  if (!snapshot && !error) return <div className="center-page"><Spinner label="Loading your earnings" /></div>;
  if (!snapshot) return <div className="error-state"><h1>We couldn't load your earnings.</h1><p>{error}</p><button className="button" onClick={load}><RefreshCw />Try again</button></div>;

  const reached = snapshot.goal?.status === "reached";
  const progress = snapshot.goal ? Math.min(snapshot.balance.availableCents, snapshot.goal.amountCents) : 0;
  return (
    <div className="earn-page">
      {api.demo && <div className="inline-demo"><Sparkles /> Demo mode: offer buttons open a safe completion simulator.</div>}
      <section className={`balance-hero ${reached ? "goal-complete" : ""}`}>
        <div className="balance-heading">
          <p className="eyebrow">AVAILABLE BALANCE</p>
          <div className="big-balance">{money(snapshot.balance.availableCents)}</div>
          {snapshot.balance.pendingCents > 0 && <p className="pending-copy">{money(snapshot.balance.pendingCents)} pending confirmation</p>}
        </div>
        {snapshot.goal && (
          <div className="goal-panel">
            {reached ? (
              <>
                <Trophy className="trophy" />
                <div><p className="eyebrow">GOAL REACHED</p><h1>You passed your {money(snapshot.goal.amountCents)} goal.</h1><p>Your goal stays in your history even if your available balance changes.</p></div>
                <button className="button button-light" onClick={() => navigate("/onboarding/goal")}>Set my next goal<ArrowRight /></button>
              </>
            ) : (
              <>
                <div className="goal-row"><span>Your {money(snapshot.goal.amountCents)} goal</span><strong>{money(progress)} / {money(snapshot.goal.amountCents)}</strong></div>
                <ProgressBar value={progress} max={snapshot.goal.amountCents} />
                <div className="gap-row"><div><strong>{money(snapshot.gapCents)}</strong><span> left to go</span></div>{recommendation && <button className="button button-light" onClick={() => start(recommendation, "gap_cta")}>Find me {money(snapshot.gapCents)}<ArrowRight /></button>}</div>
              </>
            )}
          </div>
        )}
      </section>

      {error && <div className="form-error page-error" role="alert">{error}</div>}

      {recommendation ? (
        <section className="recommended-section">
          <div className="section-heading-row"><div><p className="eyebrow">FASTEST WAY THERE</p><h2>Your best next move</h2></div><p>Chosen to move you closest to your goal.</p></div>
          <OfferCard offer={recommendation} featured onStart={(offer) => start(offer, "recommendation")} busy={busyOffer === recommendation.id} />
        </section>
      ) : !reached ? (
        <section className="empty-panel"><CheckCircle2 /><h2>You're caught up for now.</h2><p>No new eligible opportunities are available. Check Activity for anything pending, or return later.</p><button className="button secondary" onClick={() => navigate("/activity")}>View activity</button></section>
      ) : null}

      <section className="more-offers">
        <div className="section-heading-row"><div><p className="eyebrow">MORE WAYS TO EARN</p><h2>Choose another opportunity</h2></div></div>
        <div className="offer-tabs" role="tablist" aria-label="Offer views">
          <button role="tab" aria-selected={view === "quick"} onClick={() => setView("quick")}>Quick wins</button>
          <button role="tab" aria-selected={view === "highest"} onClick={() => setView("highest")}>Highest paying</button>
          <button role="tab" aria-selected={view === "free"} onClick={() => setView("free")}>No purchase required</button>
        </div>
        {secondary.length ? <div className="offer-grid">{secondary.map((offer) => <OfferCard key={offer.id} offer={offer} onStart={start} busy={busyOffer === offer.id} />)}</div> : <p className="muted-empty">No additional offers in this view right now.</p>}
      </section>
    </div>
  );
}
